<template>
  <form>
    <div class="row mb-2">
      <div class="col-sm-12">

        <div class="row mt-2">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.amount") }}:</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`amount`)" >
              <input v-model="form.amount"
                     :class="parentProps.class"
                     type="text" class="form-control " id="amount">
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-4">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.from_account") }}:</label>
          <div class="col-sm-8">
            <button type="button" class="btn w-100 btn-outline-dark text-capitalize-first"  @click.prevent="toggleFromType()">{{ helper.trans(`labels.${form.fromAccountType}`)  }}</button>
          </div>
        </div>
        <div class="row mt-2" v-if="form.fromAccountType !== 'system'">
          <div class="col-sm-8 offset-sm-4">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`fromAccountId`)">
              <Multiselect
                :class="parentProps.class"
                v-model="form.fromAccount"
                :options="fromAccounts"
                :custom-label="(opt) => labelFor(form.fromAccountType, opt)"
                track-by="id"
                select-label="Select"
                open-direction="bottom"
                :multiple="false"
                :searchable="true"
                :loading="isLoading"
                :internal-search="false"
                :clear-on-select="true"
                :close-on-select="true"
                :options-limit="100"
                :limit="1000"
                :show-no-results="false"
                :hide-selected="false"
                @search-change="asyncFindFromAccounts"
              >

              </Multiselect>
            </FormInputWrapper>
          </div>
        </div>
        <div class="row mt-2" v-if="form.fromAccount">
          <div class="col-sm-8 offset-sm-4">
            <div class="d-flex justify-content-center">
              <AccountWidget :show-icon="true" :account="form.fromAccount?.hasOwnProperty('account') ? form.fromAccount.account : form.fromAccount"></AccountWidget>
            </div>

          </div>
        </div>

        <div class="row mt-4">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.to_account") }}:</label>
          <div class="col-sm-8">
            <button type="button" class="btn w-100 btn-outline-dark text-capitalize-first"  @click.prevent="toggleToType()">{{ helper.trans(`labels.${form.toAccountType}`)  }}</button>
          </div>
        </div>
        <div class="row mt-2" v-if="form.toAccountType !== 'system'">
          <div class="col-sm-8 offset-sm-4">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`toAccountId`)">
              <Multiselect
                :class="parentProps.class"
                v-model="form.toAccount"
                :options="toAccounts"
                :custom-label="(opt) => labelFor(form.toAccountType, opt)"
                track-by="id"
                select-label="Select"
                open-direction="bottom"
                :multiple="false"
                :searchable="true"
                :loading="isLoading"
                :internal-search="false"
                :clear-on-select="true"
                :close-on-select="true"
                :options-limit="100"
                :limit="1000"
                :show-no-results="false"
                :hide-selected="false"
                @search-change="asyncFindToAccounts"
              >

              </Multiselect>
            </FormInputWrapper>
          </div>
        </div>
        <div class="row mt-2" v-if="form.toAccount">
          <div class="col-sm-8 offset-sm-4">
            <div class="d-flex justify-content-center">
              <AccountWidget :show-icon="true" :account="form.toAccount?.hasOwnProperty('account') ? form.toAccount.account : form.toAccount"></AccountWidget>
            </div>
          </div>
        </div>

      </div>
    </div>

  </form>
</template>

<script lang="ts">
import {defineComponent, toRefs, reactive, onMounted} from "vue";
import {useStore} from "vuex";
import {debounce, helper} from "@/service/CommonHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import ValidationErrorDTO from "@/models/ValidationErrorDTO";
import {api} from "@/service/APIClient";
import {useNotification} from "@kyvg/vue3-notification";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import Multiselect from "vue-multiselect";
import {Direction} from "@/models/enum/Direction";
import InternalTransferRequestCreateRequestDTO from "@/models/request/InternalTransferRequestCreateRequestDTO";
import InternalTransferRequestFormDTO from "@/models/form/InternalTransferRequestFormDTO";
import TraderAccountDTO from "@/models/TraderAccountDTO";
import PaginationDTO from "@/models/PaginationDTO";
import AccountWidget from "@/components/AccountWidget.vue";
import MerchantStoreAccountDTO from "@/models/MerchantStoreAccountDTO";
import UserAccountDTO from "@/models/UserAccountDTO";
import AccountDTO from "@/models/AccountDTO";
import {UserRole} from "@/models/enum/UserRole";

const types = ['system', 'trader', 'teamlead', 'referral', 'store'];

const notification = useNotification()
export default defineComponent({
  props: {
    role: String
  },
  components: {
    AccountWidget,
    Multiselect,
    FormInputWrapper,
  },
  setup() {
    const store = useStore();
    const state = reactive({
      systemAccount: null as AccountDTO|null,
      isLoading: false as boolean,
      fromAccounts: [] as TraderAccountDTO[]|MerchantStoreAccountDTO[]|UserAccountDTO[],
      toAccounts: [] as TraderAccountDTO[]|MerchantStoreAccountDTO[]|UserAccountDTO[],
      errors: {} as ValidationErrorDTO[]|any,
      form: {} as InternalTransferRequestFormDTO,
    });

    const setErrors = (data: ValidationErrorDTO[]) => {
      state.errors = {};
      for (const error of data) {
        if (!state.errors.hasOwnProperty(error.field)) {
          state.errors[error.field] = [];
        }
        state.errors[error.field].push(error)
      }
    }

    const getErrors = (path: string) => {
      if (!state.errors.hasOwnProperty(path)) {
        return [];
      }

      return state.errors[path]
    }

    const save = () => {
      setErrors([]);
      const form = getForm();

      let promise;
      const data = {
        amount: form.amount,
        fromAccountId: form.fromAccount?.id,
        toAccountId: form.toAccount?.id
      } as InternalTransferRequestCreateRequestDTO;

      promise = api.call.internalTransferRequests.create(data)

      promise
        .catch((e: any) => {
          if (e.response && e.request && e.request.status === 400 && e.response.data) {
            setErrors(e.response.data)
          } else {
            console.log(e);
          }

        })

      return promise
    }

    const hasErrors = (path: string) => {
      return state.errors[path]?.length > 0
    }

    const getForm = (): InternalTransferRequestFormDTO => {
      return state.form
    }

    const setForm = async (form: InternalTransferRequestFormDTO) => {
      state.errors = {}
      state.form = {
        ...form,
      }
      state.systemAccount = await api.accountsGet('00000000-0000-0000-0000-000000000000')
      if (state.form.fromAccountType === 'system') {
        state.form.fromAccount = {...state.systemAccount} as AccountDTO
      }
      if (state.form.toAccountType === 'system') {
        state.form.toAccount = {...state.systemAccount} as AccountDTO
      }
    }

    const asyncFindFromAccounts = debounce(async (query) => {
      state.isLoading = true
      switch (state.form.fromAccountType) {
        case 'trader':
          state.fromAccounts = (await api.traderAccounts({limit: 100, offset: 0} as PaginationDTO, {query: query})).items;
          break;
        case 'teamlead':
          state.fromAccounts = (await api.userAccounts({limit: 100, offset: 0} as PaginationDTO, {query: query, userRole: UserRole.TRADER_TEAM_LEAD})).items;
          break;
        case 'referral':
          state.fromAccounts = (await api.userAccounts({limit: 100, offset: 0} as PaginationDTO, {query: query, userRole: UserRole.REFERRAL})).items;
          break;
        case 'store':
          state.fromAccounts = (await api.merchantStoreAccounts({limit: 100, offset: 0} as PaginationDTO, {query: query})).items;
          break;
      }

      state.isLoading = false
    }, 500);

    const asyncFindToAccounts = debounce(async (query) => {
      state.isLoading = true
      switch (state.form.toAccountType) {
        case 'trader':
          state.toAccounts = (await api.traderAccounts({limit: 100, offset: 0} as PaginationDTO, {query: query})).items;
          break;
        case 'teamlead':
          state.toAccounts = (await api.userAccounts({limit: 100, offset: 0} as PaginationDTO, {query: query, userRole: UserRole.TRADER_TEAM_LEAD})).items;
          break;
        case 'referral':
          state.toAccounts = (await api.userAccounts({limit: 100, offset: 0} as PaginationDTO, {query: query, userRole: UserRole.REFERRAL})).items;
          break;
        case 'store':
          state.toAccounts = (await api.merchantStoreAccounts({limit: 100, offset: 0} as PaginationDTO, {query: query})).items;
          break;
      }
      state.isLoading = false
    }, 500);

    const toggleFromType = () => {
      const index = state.form.fromAccountType ? types.indexOf(state.form.fromAccountType) : 0;
      let newValue = types[index + 1];
      state.form.fromAccountType = newValue ?? types[0]
      state.fromAccounts = []
      state.form.fromAccount = null;
      if (state.form.fromAccountType === 'system') {
        state.form.fromAccount = {...state.systemAccount} as AccountDTO
      }
    }

    const toggleToType = () => {
      const index = state.form.toAccountType ? types.indexOf(state.form.toAccountType) : 0;
      let newValue = types[index + 1];
      state.form.toAccountType = newValue ?? types[0]
      state.toAccounts = []
      state.form.toAccount = null;
      if (state.form.toAccountType === 'system') {
        state.form.toAccount = {...state.systemAccount} as AccountDTO
      }
    }

    const labelFor = (type: string, opt: TraderAccountDTO|UserAccountDTO|MerchantStoreAccountDTO) => {
      if (type === 'store') {
        const storeAccount = opt as MerchantStoreAccountDTO
        return `${storeAccount.user.name} (${storeAccount.user.email}) - ${storeAccount.storeName}`
      }

      return `${opt.user.name} (${opt.user.email})`
    }

    onMounted(async () => {
    })

    return {
      ...toRefs(state),
      getForm,
      setForm,
      store,
      helper,
      numberFormatter,
      setErrors,
      getErrors,
      hasErrors,
      save,
      Direction,
      asyncFindFromAccounts,
      asyncFindToAccounts,
      toggleFromType,
      toggleToType,
      labelFor
    }
  }
})
</script>
